import { ReactNode, useState } from 'react';
import { Image, Text } from '@belong/ui';
import clsx from 'clsx';
import css from './step-card.module.css';

export type StepCardConstantProps = {
  src: string;
  title: ReactNode;
  subtitle?: ReactNode;
  subtitleClassName?: string;
  variant?: 'default' | 'medium' | 'small';
  hoverState?: ReactNode;
  showNumbers?: boolean;
};

type StepCardProps = StepCardConstantProps & { step: number };

export function StepCard({
  step,
  src,
  title,
  subtitle,
  hoverState,
  subtitleClassName,
  variant = 'default',
  showNumbers = true,
}: StepCardProps) {
  const [showHover, setShowHover] = useState(false);
  const [singleTouch, setSingleTouch] = useState(false);

  function getTextVariant() {
    if (variant === 'medium' || variant === 'small') {
      return 'body';
    }
    return 'h3';
  }

  return (
    <div
      className={clsx(
        css.container,
        'relative flex w-full flex-col items-center text-center',
      )}
    >
      {showNumbers && (
        <div
          className={clsx(
            css.stepNumberContainer,
            'mb-sm bg-navy flex items-center justify-center rounded-full',
          )}
        >
          <Text variant="h3" fontWeight="semibold" className="text-white">
            {step}
          </Text>
        </div>
      )}
      {src && (
        <div
          className={clsx(
            'aspect-h-2 aspect-w-3 mb-xs',
            variant === 'small'
              ? css.imageSmall
              : variant === 'medium'
                ? css.imageMedium
                : css.image,
          )}
        >
          <Image src={src} alt="" />
        </div>
      )}
      <div className={variant === 'small' ? 'mb-xs' : 'mb-xs md:mb-sm'}>
        <Text as="div" variant={getTextVariant()} fontWeight="semibold">
          {title}
        </Text>
      </div>
      {subtitle && (
        <div className="mb-xs md:mb-sm">
          <Text className={subtitleClassName}>{subtitle}</Text>
        </div>
      )}
      {hoverState && (
        <>
          <div
            className={clsx(
              css.hoverIndicatorContainer,
              'border-navy relative rounded-full border-2 border-solid',
            )}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              {Array.from(Array(3), (_, index) => (
                <div
                  key={index}
                  className={clsx(
                    css.hoverIndicatorDot,
                    'bg-navy rounded-full last:mr-0',
                  )}
                />
              ))}
            </div>
          </div>
          <div
            onTouchStart={() => setSingleTouch(true)}
            onTouchMove={() => setSingleTouch(false)}
            onTouchEnd={() => {
              if (singleTouch) setShowHover(!showHover);
            }}
            onMouseEnter={() => setShowHover(true)}
            onMouseLeave={() => setShowHover(false)}
            className={clsx(
              'duration-hover absolute inset-0 cursor-pointer opacity-0 transition xl:cursor-default',
              showHover && 'opacity-100',
            )}
          >
            {hoverState}
          </div>
        </>
      )}
    </div>
  );
}

export type HoverStateProps = {
  title: ReactNode;
  body: string;
  action?: ReactNode;
  showNumbers?: boolean;
};

function HoverState({ title, body, action, showNumbers }: HoverStateProps) {
  return (
    <div className="flex size-full items-center justify-center bg-white/90 text-center">
      <div
        className={clsx(
          showNumbers
            ? css.hoverStateContent
            : css.hoverStateContentWithoutNumbers,
          'flex flex-col items-center',
        )}
      >
        <div className="mb-xs">
          <Text as="div" fontWeight="semibold">
            {title}
          </Text>
        </div>
        <Text as="div">{body}</Text>
        {action && (
          <div className="relative w-full">
            <div className="pt-sm absolute inset-x-0 top-0">{action}</div>
          </div>
        )}
      </div>
    </div>
  );
}

StepCard.HoverState = HoverState;
